import { memo, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import type { Booking, TripStatus } from '../../lib/types';
import { AirlineLogo } from '../ui/AirlineLogo';
import { Sparkline } from '../ui/Sparkline';
import { isFlightPast, isMonitorable } from '../../lib/utils/booking';
import { computeLegs, detectTripType } from '../../lib/utils/trip-type';
import { getAirlineName } from '../../lib/constants';
import { formatPrice } from '../../lib/utils/savings';
import { formatWatchingDuration } from '../../lib/utils/time';
import { RouteDisplay } from '../ui/RouteDisplay';
import { StatusDot } from '@perch/ui';
import { BookingDetailContent } from '../bookings/BookingDetailModal';

// Stagger delay per card index (seconds, for Framer Motion)
const ANIMATION_STAGGER_S = 0.08;

interface TripCardProps {
  booking: Booking;
  index: number;
  onClick: (booking: Booking) => void;
  isExpanded?: boolean;
  onStopWatching?: (bookingId: string) => Promise<boolean>;
  onDeleteBooking?: (bookingId: string) => Promise<boolean>;
  onCollapse?: () => void;
  /** Price history for sparkline visualization. Prices in chronological order. */
  priceHistory?: number[];
}

/**
 * Trip status state machine. Priority order matters: rebooked (terminal
 * success) > departed (time-based) > stopped (user action) > saved
 * (non-monitorable) > watching (default).
 *
 * drop_found is now internal-only (admin dashboard) — users see "watching"
 * until a flight is rebooked, per the Perch Paradox. Showing price drops
 * creates "why haven't you acted?" anxiety that erodes trust.
 */
function getTripStatus(booking: Booking): TripStatus {
  if (booking.booking_status === 'rebooked') {
    return 'rebooked';
  }
  if (isFlightPast(booking.segments)) {
    return 'departed';
  }
  if (!booking.is_active) {
    return 'stopped';
  }
  if (!isMonitorable(booking)) {
    return 'saved';
  }
  // drop_found is now internal-only — users see "watching" until rebooked
  return 'watching';
}

function getSavedReason(booking: Booking): string {
  return `${getAirlineName(booking.airline_code)} monitoring coming soon`;
}

/**
 * Status indicator component with custom SVG icons
 * Icons per spec: ✓ watching, $ drop_found, sparkle rebooked, plane departed
 */
function StatusIndicator({ status, watchingSince }: { status: TripStatus; watchingSince?: string | null }) {
  switch (status) {
    case 'rebooked':
      return (
        <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium">Rebooked</span>
        </div>
      );
    case 'departed':
      return (
        <div className="flex items-center gap-1.5 text-stone-400 dark:text-stone-500">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
          </svg>
          <span className="text-sm">Departed</span>
        </div>
      );
    case 'stopped':
      return (
        <div className="flex items-center gap-1.5 text-stone-400 dark:text-stone-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <span className="text-sm">Stopped</span>
        </div>
      );
    case 'saved':
      return (
        <div className="flex items-center gap-1.5 text-stone-400 dark:text-stone-500">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
          </svg>
          <span className="text-sm">Saved</span>
        </div>
      );
    case 'watching':
    default:
      return (
        <div className="flex items-center gap-1.5 text-stone-500 dark:text-stone-400">
          <StatusDot />
          <span className="text-sm">
            Watching{watchingSince !== undefined && (
              <> · {formatWatchingDuration(watchingSince)}</>
            )}
          </span>
        </div>
      );
  }
}

/**
 * Premium trip card with inline expansion for booking details.
 * Clicking toggles between summary and expanded detail view.
 * Uses CSS grid-rows transition for smooth height animation.
 */
export const TripCard = memo(function TripCard({
  booking,
  index,
  onClick,
  isExpanded = false,
  onStopWatching,
  onDeleteBooking,
  onCollapse,
  priceHistory,
}: TripCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const legs = computeLegs(booking.segments ?? []);
  const tripType = detectTripType(legs);
  const isRoundtripBooking = tripType === 'roundtrip';

  // For roundtrips, show the outbound leg's origin->destination (not origin->origin)
  const origin = legs[0]?.origin ?? booking.segments?.[0]?.origin;
  const destination = isRoundtripBooking
    ? legs[0]?.destination
    : legs[legs.length - 1]?.destination ?? booking.segments?.[booking.segments?.length - 1]?.destination;

  const departureDate = legs[0]?.departureDate
    ? new Date(legs[0].segments[0].departure_datetime)
    : booking.segments?.[0]
      ? new Date(booking.segments[0].departure_datetime)
      : null;
  const returnDate = isRoundtripBooking && legs[1]
    ? new Date(legs[1].segments[0].departure_datetime)
    : null;

  const status = getTripStatus(booking);
  const isActive = booking.is_active && status === 'watching';
  const isSaved = status === 'saved';
  const isRebooked = status === 'rebooked';

  // Auto-scroll expanded card into view
  useEffect(() => {
    if (isExpanded && cardRef.current) {
      const timer = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300, delay: index * ANIMATION_STAGGER_S }}
      className={`
        group relative bg-white dark:bg-stone-900 rounded-2xl
        border transition-[border-color,box-shadow] duration-300
        ${isExpanded
          ? 'border-stone-300 dark:border-stone-600 shadow-lg shadow-stone-900/5 dark:shadow-black/20'
          : `border-stone-200/60 dark:border-stone-800/80 hover:border-stone-300 dark:hover:border-stone-700 hover:shadow-xl hover:shadow-stone-900/5 dark:hover:shadow-black/20`
        }
        ${isRebooked && !isExpanded ? 'border-emerald-300/60 dark:border-emerald-700/40 drop-animate glow-pulse' : ''}
      `}
    >
      {/* Ambient gradient for rebooked state */}
      {isRebooked && (
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/8 via-transparent to-emerald-500/4 pointer-events-none rounded-2xl" />
      )}

      {/* Summary row — always visible, acts as toggle */}
      <div
        className={`relative p-5 sm:p-6 cursor-pointer ${!isExpanded ? 'trip-card-press' : ''}`}
        onClick={() => onClick(booking)}
        role="button"
        aria-expanded={isExpanded}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(booking);
          }
        }}
      >
        <div className="flex items-start gap-4">
          {/* Airline logo */}
          <div className="flex-shrink-0">
            <AirlineLogo code={booking.airline_code} size={56} />
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Route left, Date right */}
            <div className="flex items-start justify-between gap-4">
              <RouteDisplay
                segments={[{ origin: origin ?? '—', destination: destination ?? '—' }]}
                tripType={tripType === 'roundtrip' ? 'round_trip' : tripType}
                size="md"
                showCityNames
              />

              {/* Date — right-aligned */}
              <div className="text-sm text-stone-500 dark:text-stone-400 text-right flex-shrink-0 pt-1">
                {departureDate
                  ? returnDate
                    ? `${departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${returnDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                    : departureDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                  : 'No date'}
              </div>
            </div>

            {/* Row 2: Status */}
            <div className="flex items-center gap-3 mt-2">
              <StatusIndicator
                status={status}
                watchingSince={isActive ? booking.created_at : undefined}
              />
              {isSaved && (
                <>
                  <span className="text-stone-300 dark:text-stone-600">·</span>
                  <span className="text-xs text-stone-400 dark:text-stone-500">
                    {getSavedReason(booking)}
                  </span>
                </>
              )}
              {!booking.is_changeable && (
                <>
                  <span className="text-stone-300 dark:text-stone-600">·</span>
                  <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded">
                    Basic Economy
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Savings badge — GROSS savings for all rebooked bookings */}
          {isRebooked && booking.potential_savings && (() => {
            const gross = booking.potential_savings;
            const displayAmount = gross;
            return (
            <div className="flex-shrink-0 text-right savings-number">
              <div className="text-[10px] uppercase tracking-widest mb-1 font-medium text-emerald-600/60 dark:text-emerald-400/60">
                saved
              </div>
              <div className="text-2xl font-data tabular-nums leading-none text-emerald-600 dark:text-emerald-400">
                {formatPrice(displayAmount, booking.is_award_booking, !booking.is_award_booking)}
              </div>
              {booking.is_award_booking && (
                <div className="text-[10px] mt-0.5 text-emerald-600/60 dark:text-emerald-400/60">pts</div>
              )}
            </div>
            );
          })()}

          {/* Chevron — rotates when expanded */}
          <div className="flex-shrink-0 self-center text-stone-300 dark:text-stone-600 group-hover:text-stone-400 dark:group-hover:text-stone-400 transition-[transform,box-shadow,border-color] duration-300">
            <svg
              className={`w-5 h-5 transition-transform duration-300 ${
                isExpanded ? 'rotate-90' : 'group-hover:translate-x-1'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Sparkline — abstract price trajectory, bottom of card */}
      {priceHistory && priceHistory.length >= 2 && !isExpanded && (
        <div className="px-5 sm:px-6 pb-3 -mt-1">
          <Sparkline
            data={priceHistory}
            variant={isRebooked ? 'rebooked' : 'watching'}
            height={20}
          />
        </div>
      )}

      {/* Expandable detail section — uses CSS grid-rows for smooth height animation */}
      <div
        className="card-expand-wrapper"
        data-expanded={isExpanded}
      >
        <div className="card-expand-inner">
          <div className="card-expand-content">
            {isExpanded && (
              <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-0">
                <div className="border-t border-stone-200/60 dark:border-stone-700/50 pt-5">
                  <BookingDetailContent
                    booking={booking}
                    onStopWatching={onStopWatching}
                    onDeleteBooking={onDeleteBooking}
                    onClose={onCollapse}
                    inline
                    priceHistory={priceHistory}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
});
